import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import APP_VERSION from "../version";
import webauthnService from "../services/webauthnService";
import "./SettingsTab.css";

const SettingsTab = () => {
  const { token } = useAuth();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await webauthnService.listCredentials(token);
      setCreds(list || []);
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  useEffect(() => {
    setSupported(webauthnService.isSupported());
    if (token) {
      setLoading(true);
      refresh().finally(() => setLoading(false));
    }
  }, [token, refresh]);

  const onRegister = async () => {
    setMessage(null);
    setError(null);
    setBusy(true);
    try {
      await webauthnService.register(token, `Device ${new Date().toLocaleString()}`);
      setMessage("This device is now registered for Face ID / passkey login.");
      await refresh();
    } catch (err) {
      console.error("[SettingsTab] register failed:", err);
      setError(err.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    setMessage(null);
    setError(null);
    setBusy(true);
    try {
      await webauthnService.deleteCredential(token, id);
      await refresh();
    } catch (err) {
      setError(err.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="settings-tab-header">
        <h2>Settings</h2>
      </div>
      <div className="settings-tab-body">
        <div className="settings-card">
          <strong>App Version</strong>
          <span>{APP_VERSION}</span>
        </div>

        <div className="settings-section-title">Passkeys / Face ID</div>

        {!supported && (
          <div className="settings-error">
            Your device or browser does not support WebAuthn passkeys.
          </div>
        )}

        {error && <div className="settings-error">{error}</div>}
        {message && <div className="settings-info">{message}</div>}

        <div className="settings-passkey-card">
          <div className="settings-passkey-body">
            <strong>Register this device</strong>
            <span>Enable Face ID / Touch ID login on this device</span>
          </div>
          <button
            type="button"
            className="settings-passkey-btn"
            onClick={onRegister}
            disabled={busy || !supported}
          >
            {busy ? "..." : "Register"}
          </button>
        </div>

        {loading && <div className="settings-placeholder">Loading...</div>}

        {creds.length > 0 && (
          <div className="settings-creds-list">
            {creds.map((c) => (
              <div key={c.id} className="settings-cred-item">
                <div className="settings-cred-info">
                  <div className="settings-cred-name">{c.name}</div>
                  <div className="settings-cred-date">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="settings-cred-delete"
                  onClick={() => onDelete(c.id)}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
